// SPDX-License-Identifier: LicenseRef-Syncanix-Proprietary
import type { StatusIncident } from './Status';
import type { CSSProperties } from 'react';

/**
 * The status page's severity vocabulary.
 *
 * ## The defect this exists for
 *
 * Every incident rendered the same grey pill on the same white card, so an
 * ACTIVE total outage looked exactly like a maintenance window that had been
 * resolved the previous afternoon. Severity is not decoration here — it is the
 * entire reason somebody opened `/status`, usually while something of theirs is
 * failing — and it was carried by nothing but a word.
 *
 * The components list had the same shape one section up: five hairline rows
 * with a green or red word at the end. On a page whose job is "which of these
 * five is broken", the broken ones were the quietest thing on it.
 *
 * ## Colour means HAPPENING NOW, not "was serious"
 *
 * The distinction the design leans on: an active incident is toned and carries
 * an accent rule, a resolved one is neutral and carries a check. That is the
 * question a visitor is actually asking, and it means a page full of history
 * reads as calm rather than as a wall of red. The severity word stays on a
 * resolved card, so nothing is hidden — it is quiet, not gone.
 *
 * ## Bedrock tokens rather than the shadcn status scale
 *
 * Same reasoning as the pricing-history cards, and the same measurement
 * discipline: the badge ground is a tint, so the ink is measured against the
 * composite rather than against the page. `status-tone.test.ts` reproduces the
 * mix from the values exported here, so retuning `SEVERITY_TINT` fails a test
 * rather than someone's eyes.
 */

export type StatusSeverity = StatusIncident['severity'];

/** Badge tint strength, and the accent rule's own (stronger — it is a 3px bar
 *  rather than a ground under text, so it is a non-text contrast case). */
export const SEVERITY_TINT = 0.14;

/**
 * ## Fills tint GROUNDS; inks draw MARKS and text
 *
 * The dot beside a component and the rule down an active card are both drawn in
 * the tone's **ink**, not its fill, and that is not a stylistic preference —
 * the first draft used the fill and `status-tone.test.ts` refused it twice.
 * `--bd-ok` measures **1.92:1** on the page and `--bd-warn` **2.08:1** on its
 * own wash, both under the 3:1 non-text floor: a mint dot and an amber rule
 * that are, in practice, decoration a reader has to hunt for. Each ink is the
 * readable member of its own family, so the mark still reads as green / amber /
 * red / blue while actually being visible.
 */
export interface StatusTone {
  /** The recognisable colour. A GROUND: tinted under things, never drawn. */
  readonly fill: string;
  /** The readable member of the same family. Text, dots and rules. */
  readonly ink: string;
}

export const SEVERITY_TONE: Readonly<Record<StatusSeverity, StatusTone>> = {
  outage: { fill: 'var(--bd-danger)', ink: 'var(--bd-danger-ink)' },
  degraded: { fill: 'var(--bd-warn)', ink: 'var(--bd-warn-ink)' },
  // Planned work is not a fault. It is informational blue rather than a third
  // shade of alarm, so a scheduled upgrade never reads as something going
  // wrong — which is the whole reason an operator announces it in advance.
  maintenance: { fill: 'var(--bd-blue)', ink: 'var(--bd-link)' },
};

/** The tone a RESOLVED incident and an operational component share. */
export const RESOLVED_TONE: StatusTone = { fill: 'var(--bd-ok)', ink: 'var(--bd-ok-ink)' };

/** The tone a DOWN component takes — the same family as an outage. */
export const DOWN_TONE: StatusTone = SEVERITY_TONE.outage;

/**
 * A drawn mark — the component row's state dot.
 *
 * A named helper rather than `{ background: tone.ink }` at the call site,
 * because the two members are one character apart and `fill` is the one that
 * reads as correct. The measured ratios are in `status-tone.test.ts`; this is
 * what stops the call site quietly choosing the other member and passing every
 * gate anyway.
 */
export function markStyle(tone: StatusTone): CSSProperties {
  return { background: tone.ink };
}

/** A tinted pill: the tone's fill as ground, its ink as text. */
export function pillStyle(tone: StatusTone): CSSProperties {
  return {
    background: `color-mix(in srgb, ${tone.fill} ${String(SEVERITY_TINT * 100)}%, var(--bd-page))`,
    color: tone.ink,
  };
}

/**
 * The paint for an ACTIVE incident card — an inline-start rule in the severity
 * fill over a barely-there ground of the same colour.
 *
 * The ground is deliberately far weaker than the badge's: it sits under body
 * prose, and prose is a text-contrast case where a badge label is a short one
 * on a known ink. At 4% the card still reads as white-ish and the rule does the
 * work, which is what keeps three stacked incidents from looking like an alarm
 * panel.
 */
export const CARD_TINT = 0.04;

export function activeCardStyle(severity: StatusSeverity): CSSProperties {
  const tone = SEVERITY_TONE[severity];
  return {
    // Ink for the rule, fill for the wash — see the note on `StatusTone`.
    borderInlineStartColor: tone.ink,
    background: `color-mix(in srgb, ${tone.fill} ${String(CARD_TINT * 100)}%, var(--bd-page))`,
  };
}
